// src/redux/store.js
import { combineReducers, configureStore } from "@reduxjs/toolkit";
import { getGlobalAccessToken } from "../context/AuthContext";
import { getWorkspaceGeneration } from "../services/workspaceSession";

import savedSearchReducer from "./savedSearchSlice";
import searchReducer from "./searchSlice";
import subscriptionReducer from "./subscriptionSlice";
import profileReducer from "./profileSlice";
import notificationReducer from "./notificationSlice";
import trendingReducer from "./trendingSlice";
import invitesReducer from "./inviteSlice";
import redemptionsReducer from "./redemptionsSlice";
import offersReducer from "./offersSlice";
import bookingsReducer from "./bookingsSlice";
import campaignsReducer from "./campaignsSlice";
import advisorReducer from "./advisorSlice";
import accountSettingsReducer from "./accountSettingsSlice";
import influencerDetailsReducer from "./influencerDetailsSlice";
import slugLookupReducer from "./slugLookupSlice";
import discoveryReducer from "./discoverySlice";
import reactionsReducer from "./reactionsSlice";
import eventsReducer from "./eventsSlice";
import unlocksReducer from "./unlocksSlice";
import searchHistoryReducer from "./searchHistorySlice";
import usageReducer from "./usageSlice";
import entitlementsReducer from "./entitlementsSlice";
import savedCreatorsReducer from "./savedCreatorsSlice";
import preferencesReducer from "./preferencesSlice";
import creatorWorkflowReducer from "./creatorWorkflowSlice";
import creatorsCrmReducer from "./creatorsCrmSlice";
import demandSuggestionsReducer from "./demandSuggestionsSlice";
import creatorRequestsReducer from "./creatorRequestSlice";
import creatorAdvisorReducer from "./creatorAdvisorSlice";
import workspaceReducer from "./workspaceSlice";
import teamReducer from "./teamSlice";


const appReducer = combineReducers({
  influencerDetails: influencerDetailsReducer,
  savedSearch: savedSearchReducer,
  search: searchReducer,
  subscription: subscriptionReducer,
  profile: profileReducer,
  notifications: notificationReducer,
  trending: trendingReducer,

  offers: offersReducer,
  campaigns: campaignsReducer,
  advisor: advisorReducer,
  invites: invitesReducer,
  redemptions: redemptionsReducer,
  bookings: bookingsReducer,

  accountSettings: accountSettingsReducer,
  slugLookup: slugLookupReducer,
  discovery: discoveryReducer,
  reactions: reactionsReducer,
  events: eventsReducer,
  unlocks: unlocksReducer,
  searchHistory: searchHistoryReducer,
  usage: usageReducer,
  entitlements: entitlementsReducer,
  preferences: preferencesReducer,
  savedCreators: savedCreatorsReducer,
  creatorWorkflow: creatorWorkflowReducer,
  creatorsCrm: creatorsCrmReducer,
  demandSuggestions: demandSuggestionsReducer,
  creatorRequests: creatorRequestsReducer,
  creatorAdvisor: creatorAdvisorReducer,
  workspace: workspaceReducer,
  team: teamReducer,
});

// Customer/client-owned state must never survive a workspace switch. Keeping
// subscription/profile/team/workspace state avoids unnecessary auth/account
// refetches while preventing Client A data from being shown under Client B.
const WORKSPACE_SCOPED_STATE_KEYS = Object.freeze([
  "influencerDetails",
  "savedSearch",
  "search",
  "offers",
  "campaigns",
  "advisor",
  "invites",
  "redemptions",
  "bookings",
  "discovery",
  "preferences",
  "reactions",
  "events",
  "unlocks",
  "searchHistory",
  "savedCreators",
  "creatorWorkflow",
  "creatorsCrm",
  "demandSuggestions",
  "creatorRequests",
  "creatorAdvisor",
]);

function resetWorkspaceScopedState(state) {
  const nextState = { ...state };
  WORKSPACE_SCOPED_STATE_KEYS.forEach((key) => {
    delete nextState[key];
  });
  // Running the reducer once more with a no-op action reinitializes only the
  // deleted slices while preserving account/workspace/entitlement state.
  return appReducer(nextState, { type: "workspace/__scopedStateReset" });
}

function rootReducer(state, action) {
  const previousWorkspaceId = state?.workspace?.activeWorkspaceId || null;
  const nextState = appReducer(state, action);
  const nextWorkspaceId = nextState?.workspace?.activeWorkspaceId || null;

  // Do not special-case only the explicit switch action. The active workspace
  // can also change when an archived/invalid persisted workspace falls back to
  // another accessible workspace. Any identity change must reset client data.
  if (state && previousWorkspaceId !== nextWorkspaceId) {
    return resetWorkspaceScopedState(nextState);
  }

  return nextState;
}

const WORKSPACE_ASYNC_PREFIXES = new Set([
  "influencerDetails",
  "savedSearch",
  "search",
  "offers",
  "campaigns",
  "advisor",
  "creatorInvites",
  "redemptions",
  "bookings",
  "discovery",
  "preferences",
  "reactions",
  "events",
  "searchHistory",
  "savedCreators",
  "creatorWorkflow",
  "creatorsCrm",
  "demandSuggestions",
  "creatorRequests",
  "creatorAdvisor",
]);

/**
 * RTK thunks from Client A may finish after a workspace switch even though the
 * underlying HTTP request was aborted. Drop those stale fulfilled/rejected
 * actions so they cannot repopulate Client B state or surface a cancelled-error
 * banner in the new workspace.
 */
function createWorkspaceAsyncGuardMiddleware() {
  const requestGenerationById = new Map();

  return () => (next) => (action) => {
    const type = String(action?.type || "");
    const prefix = type.split("/", 1)[0];
    const requestId = action?.meta?.requestId;

    if (!requestId || !WORKSPACE_ASYNC_PREFIXES.has(prefix)) {
      return next(action);
    }

    if (type.endsWith("/pending")) {
      requestGenerationById.set(requestId, getWorkspaceGeneration());
      return next(action);
    }

    if (type.endsWith("/fulfilled") || type.endsWith("/rejected")) {
      const startedGeneration = requestGenerationById.get(requestId);
      requestGenerationById.delete(requestId);

      if (
        startedGeneration !== undefined &&
        startedGeneration !== getWorkspaceGeneration()
      ) {
        return action;
      }
    }

    return next(action);
  };
}

const workspaceAsyncGuardMiddleware = createWorkspaceAsyncGuardMiddleware();

const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      thunk: { extraArgument: { getAccessToken: getGlobalAccessToken } },
      serializableCheck: false,
    }).prepend(workspaceAsyncGuardMiddleware),
});

export default store;
